import type { Bar } from '../chart/chartModel';

/** A computed series aligned with the bars: null where the indicator has no value yet. */
export type Series = (number | null)[];

export const APPLIED_PRICES = ['close', 'open', 'high', 'low', 'median', 'typical', 'weighted'] as const;
export type AppliedPrice = (typeof APPLIED_PRICES)[number];

export const MA_METHODS = ['sma', 'ema', 'smma', 'lwma'] as const;
export type MaMethod = (typeof MA_METHODS)[number];

export function priceOf(bar: Bar, applied: AppliedPrice): number {
  switch (applied) {
    case 'close':
      return bar.c;
    case 'open':
      return bar.o;
    case 'high':
      return bar.h;
    case 'low':
      return bar.l;
    case 'median':
      return (bar.h + bar.l) / 2;
    case 'typical':
      return (bar.h + bar.l + bar.c) / 3;
    case 'weighted':
      return (bar.h + bar.l + 2 * bar.c) / 4;
  }
}

export const appliedSeries = (bars: Bar[], applied: AppliedPrice): number[] =>
  bars.map((b) => priceOf(b, applied));

const empty = (n: number): Series => new Array<number | null>(n).fill(null);

/** Simple moving average; the first value is at index period-1. */
export function sma(values: (number | null)[], period: number): Series {
  const out = empty(values.length);
  let sum = 0;
  let count = 0;
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (v === null || v === undefined) {
      sum = 0;
      count = 0;
      continue;
    }
    sum += v;
    count += 1;
    if (count > period) sum -= values[i - period]!;
    if (count >= period) out[i] = sum / period;
  }
  return out;
}

/**
 * Exponential moving average, seeded with the first price like MT5, so it has a value from the first bar
 * (early values are approximations that settle after a few periods).
 */
export function ema(values: (number | null)[], period: number): Series {
  const out = empty(values.length);
  const k = 2 / (period + 1);
  let prev: number | null = null;
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (v === null || v === undefined) continue;
    prev = prev === null ? v : v * k + prev * (1 - k);
    out[i] = prev;
  }
  return out;
}

/** Smoothed moving average (Wilder): seeded with the simple average of the first `period` values. */
export function smma(values: (number | null)[], period: number): Series {
  const out = empty(values.length);
  let prev: number | null = null;
  let sum = 0;
  let count = 0;
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (v === null || v === undefined) continue;
    if (prev === null) {
      sum += v;
      count += 1;
      if (count === period) {
        prev = sum / period;
        out[i] = prev;
      }
    } else {
      prev = (prev * (period - 1) + v) / period;
      out[i] = prev;
    }
  }
  return out;
}

/** Linear weighted moving average: the newest value has weight `period`, the oldest 1. */
export function lwma(values: (number | null)[], period: number): Series {
  const out = empty(values.length);
  const divisor = (period * (period + 1)) / 2;
  for (let i = period - 1; i < values.length; i++) {
    let sum = 0;
    let ok = true;
    for (let k = 0; k < period; k++) {
      const v = values[i - period + 1 + k];
      if (v === null || v === undefined) {
        ok = false;
        break;
      }
      sum += v * (k + 1);
    }
    if (ok) out[i] = sum / divisor;
  }
  return out;
}

export function movingAverage(values: (number | null)[], period: number, method: MaMethod): Series {
  switch (method) {
    case 'sma':
      return sma(values, period);
    case 'ema':
      return ema(values, period);
    case 'smma':
      return smma(values, period);
    case 'lwma':
      return lwma(values, period);
  }
}

/** Highest of the last `period` values ending at `i` (inclusive). */
export const highest = (values: number[], i: number, period: number): number => {
  let m = -Infinity;
  for (let k = Math.max(0, i - period + 1); k <= i; k++) m = Math.max(m, values[k]!);
  return m;
};
export const lowest = (values: number[], i: number, period: number): number => {
  let m = Infinity;
  for (let k = Math.max(0, i - period + 1); k <= i; k++) m = Math.min(m, values[k]!);
  return m;
};

/** True range: the widest of high-low and the gaps to the previous close. The first bar has only high-low. */
export function trueRange(bars: Bar[]): number[] {
  return bars.map((b, i) => {
    if (i === 0) return b.h - b.l;
    const prev = bars[i - 1]!.c;
    return Math.max(b.h, prev) - Math.min(b.l, prev);
  });
}
